import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Grid, Typography, FormControlLabel, Radio, Switch } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import { PinnedIcon } from 'containers/patient/tasks-new/tasks-table/tooltips/note-tooltip';
import RadioGroup from 'components/form/radio/radio-group';
import { LookupNoteTagType } from 'constants/enums';

import RadioButton from 'components/form/radio/radio-button';
import Validation from 'components/form/validation/validation';
import { useDispatch } from 'react-redux';
import { noteTagTypes } from 'constants/lists';
import { change } from 'redux-form';
import FieldLabel from 'components/form/field/field-label';

import Mentions from 'rc-mentions';

import lodash from 'lodash';
import { renderReactRadioGroup as RenderRadioGroup } from 'components/form/field/redux-field';

import { styles } from './note-styles';
import { useTypedSelector } from '../../hooks/use-typed-selector';

const INPUT_DEBOUNCE_MS = 5;

const NoteArea = props => {
  const {
    input,
    data,
    disabled,
    maxLength,
    defaultPlaceholder,
    id,
    'data-qa-id': dataQaId,
    meta,
    autoFocus,
    className,
    formId,
    classes,
    hidePin = false,
    field,
  } = props;

  const isRequired = field?.required;
  const placeholder = `${defaultPlaceholder || 'Add a Note'}${isRequired ? ' *' : ''}`;

  const dispatch = useDispatch();

  const [val, setVal] = useState(input.value);
  const [isPinned, setIsPinned] = useState(false);
  const [selectedNotePinIndex, setSelectedNotePinIndex] = useState(null);

  const onChangeNotePin = e => {
    setSelectedNotePinIndex(e);
    dispatch(change(formId, 'note_pin', e));
  };

  const handleSwitch = () => {
    if (isPinned) {
      dispatch(change(formId, 'note_pin', null));
      setSelectedNotePinIndex(null);
      setIsPinned(false);
    } else {
      dispatch(change(formId, 'note_pin', 1));
      setSelectedNotePinIndex(1);
      setIsPinned(true);
    }
  };

  const noteBar = useTypedSelector(state => state.noteBar);
  const lookupStatusReasons = useTypedSelector(state => state.lookups.lookupStatusReasons);
  const forms = useTypedSelector(state => state.form);
  const tasks = useTypedSelector(state => state.tasks.data);
  const tagType = noteTagTypes.find(tag => tag.value === noteBar.tagTypeId);
  const tagTypeLabel = tagType?.label;
  const task = tasks[`${tagTypeLabel}${noteBar?.tagResourceId}`];
  const debouncedFnRef = useRef();

  useEffect(() => {
    debouncedFnRef.current = lodash.debounce(value => {
      input.onChange(value);
    }, props.debounceOverride || INPUT_DEBOUNCE_MS);

    return () => {
      debouncedFnRef.current?.cancel();
    };
  }, [input, props.debounceOverride]);

  const handleChange = useCallback(e => {
    setVal(e);
    debouncedFnRef.current?.(e);
    if (e.length === 0) {
      setSelectedNotePinIndex(null);
      dispatch(change(formId, 'note_pin', null));
      dispatch(change(formId, 'note', null));
    }
  }, []);

  useEffect(() => {
    if (input.value !== val) {
      setVal(input.value);
    }
  }, [input.value]);

  const ref = useRef();
  useEffect(() => {
    if (!autoFocus) {
      return;
    }

    setTimeout(() => {
      ref?.current?.focus();
    }, 1000);
  }, [noteBar.display]);

  let options = [];
  if (Array.isArray(data)) {
    options = data.map(d => ({
      value: d.username,
      label: d.display_name,
    }));
  }

  const getNotePinOptions = () => {
    const tagTypeFromNoteBar = noteBar.display ? Number(noteBar.tagTypeId) : null;
    const tagTypeFromForm =
      forms &&
      forms[formId] &&
      forms[formId].initial &&
      lookupStatusReasons.find(t => t.id === forms[formId].initial.status_id)
        ? LookupNoteTagType[
            String(
              lookupStatusReasons.find(t => t.id === forms[formId].initial.status_id).resource,
            ).toUpperCase()
          ]
        : null;

    const isTherapyTask =
      [19, 22].some(t => t === tagTypeFromForm) && forms && forms[formId] && forms[formId].initial
        ? forms[formId].initial.therapy_id
        : true;
    let retArr = [];
    if (tagTypeFromNoteBar === 1) {
      retArr = [
        {
          label: 'Patient',
          value: 3,
        },
      ];
    } else if (tagTypeFromNoteBar === 2) {
      retArr = [
        {
          label: 'Therapy',
          value: 2,
        },
        {
          label: 'Patient',
          value: 3,
        },
      ];
    } else if (
      [21, 24, 26].some(val => val === tagTypeFromNoteBar || val === tagTypeFromForm) ||
      formId === 'add_appointment_referral_form'
    ) {
      retArr = [
        {
          label: 'This Task',
          value: 1,
        },
        {
          label: 'Patient',
          value: 3,
        },
        {
          label: 'All Tasks',
          value: 4,
        },
      ];
    } else if (
      (noteBar.display && task && !task.therapy_id) ||
      (!noteBar.display && tagTypeFromForm && !isTherapyTask)
    ) {
      retArr = [
        {
          label: 'This Task',
          value: 1,
        },
        {
          label: 'Patient',
          value: 3,
        },
        {
          label: 'All Tasks',
          value: 4,
        },
      ];
    } else {
      retArr = [
        {
          label: 'This Task',
          value: 1,
        },
        {
          label: 'Therapy',
          value: 2,
        },
        {
          label: 'Patient',
          value: 3,
        },
        {
          label: 'All Tasks',
          value: 4,
        },
        {
          label: 'All Tasks For therapy',
          value: 5,
        },
      ];
    }
    return retArr;
  };

  return (
    <>
      <Mentions
        onFocus={() => {
          input.onFocus();
          props?.inputProps?.onFocus?.();
        }}
        onBlur={() => {
          input.onBlur();
          props?.inputProps?.onBlur?.();
        }}
        value={val}
        rows={3}
        ref={ref}
        maxLength={maxLength}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        id={id}
        data-qa-id={dataQaId}
        onChange={handleChange}
        options={options}
      />
      {!hidePin && (
        <Grid alignItems="center" justifyContent="flex-start" container>
          <Grid xs={1}>
            <FieldLabel fieldName="Pinned" />
            <Switch
              color="primary"
              onChange={handleSwitch}
              value="pinnedCheckbox"
              label="Secondary"
              inputProps={{ 'aria-label': 'controlled' }}
            />
          </Grid>
          <Grid xs={2} key="note-pin-level-text" display="inline-flex" alignItems="center">
            <PinnedIcon viewBox="-5 -5 24 24" fillColor="#70777A" />
            <Typography>Note Pinned to: </Typography>
          </Grid>
          <Grid item xs={9}>
            <Grid container>
              <RadioGroup
                horizontal
                onChange={e => onChangeNotePin(e)}
                className={classes.radioGroup}
              >
                {getNotePinOptions().map(option => {
                  return (
                    <RadioButton
                      // key={option.value}
                      value={option.value}
                      checked={option.value === selectedNotePinIndex}
                      disabled={!isPinned}
                    >
                      {option.label}
                    </RadioButton>
                  );
                })}
              </RadioGroup>
            </Grid>
          </Grid>
        </Grid>
      )}

      <Validation touched={meta.touched} error={meta.error} warning={meta.warning} />
    </>
  );
};

export default withStyles(styles)(NoteArea);
